import React, {useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import {VStack, Image} from '@salesforce/retail-react-app/app/components/shared/ui'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import {isGoogleMapsLibraryLoaded} from '@app/components/google-maps'

/**
 * GooglePlacesSuggestions is a React functional component that provides location-based autocomplete suggestions
 * using the Google Places API. It listens for changes in the input prop and fetches relevant place predictions
 * when the input is non-empty and the Google Maps Places library is loaded. Suggestions are displayed in a list,
 * and clicking on a suggestion triggers the onSuggestionClick callback with the selected suggestion object.
 * The component also displays a "Powered by Google" image to indicate the source of the data.
 *
 * Props:
 * @param {string} input - The current user input to be used for fetching place suggestions.
 * @param {function} onSuggestionClick - Callback function invoked when a suggestion is selected. Receives the selected suggestion as an argument.
 *
 * State:
 * @state {Array} suggestions - An array of suggestion objects returned from the Google Places API.
 *
 * Usage:
 * Render this component within a parent component, passing the current input value and a handler for suggestion selection.
 *
 * Dependencies:
 * - Requires the Google Maps JavaScript API with the Places library loaded.
 * - Utilizes external helper functions: isGoogleMapsLibraryLoaded and getAssetUrl.
 * - Uses Chakra UI's VStack and Image components for layout and styling.
 */
const GooglePlacesSuggestions = ({input, onSuggestionClick}) => {
    const [suggestions, setSuggestions] = useState([])

    useEffect(() => {
        const fetchSuggestions = async () => {
            if (input === '') {
                setSuggestions([])
                return
            }

            if (isGoogleMapsLibraryLoaded('places') && input) {
                const sessionToken = new window.google.maps.places.AutocompleteSessionToken()
                const request = {
                    input,
                    sessionToken: sessionToken
                }

                try {
                    const {suggestions} =
                        await window.google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(
                            request
                        )

                    setSuggestions(suggestions)
                } catch (error) {
                    console.error('Error fetching place predictions:', error)
                }
            }
        }

        fetchSuggestions()
    }, [input])

    return (
        <VStack className="googleplaces-suggestions" alignItems="flex-start" pt={5} pb={12}>
            {suggestions.length > 0 && (
                <ul className="googleplaces-list">
                    {suggestions.map((suggestion, index) => (
                        <li key={index} className="googleplaces-item">
                            <button
                                className="googleplaces-option"
                                onClick={() => {
                                    setSuggestions([])
                                    onSuggestionClick(suggestion)
                                }}
                            >
                                {suggestion.placePrediction.text.toString()}
                            </button>
                        </li>
                    ))}
                </ul>
            )}
            <Image
                width={59}
                src={getAssetUrl('static/img/google_on_white.png')}
                alt="Powered by Google"
            />
        </VStack>
    )
}

GooglePlacesSuggestions.propTypes = {
    input: PropTypes.string.isRequired,
    onSuggestionClick: PropTypes.func.isRequired
}

export default GooglePlacesSuggestions
